import { isNil } from '@flowio/is';
import isEqual from 'lodash/isEqual';
import fetchPaymentRequest from '../services/paymentRequest/fetchPaymentRequest';
import fetchInternalPaymentRequest from '../services/paymentRequest/fetchInternalPaymentRequest';
import { DomReference, PaymentRequestCallbackQuery } from '../types';
import createInlineCallbackListener from '../utilities/createInlineCallbackListener';
import { createExecuteScriptElement } from '../utilities/createActionDomElements';
import { hideLoadingSpinner, showLoadingSpinner } from '../utilities/loadingSpinner';
import pollForPrUpdate from '../utilities/pollForPrUpdate';
import { removeElement, resolveElement } from '../utilities/elementHelpers';
import isInternalPaymentRequest from '../validators/isInternalPaymentRequest';

export default function handleExecuteScriptAction(
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  action: io.flow.v0.models.ActionExecuteScript,
  container: DomReference,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  const resolvedContainer = resolveElement(container);
  if (isNil(resolvedContainer)) {
    // eslint-disable-next-line no-console
    console.error('handleExecuteScriptAction: Mounting container was not set.');
    return Promise.reject(new Error('handleExecuteScriptAction: Mounting container was not set.'));
  }

  showLoadingSpinner({ container });

  const callbackPromise = createInlineCallbackListener<PaymentRequestCallbackQuery>({
    expiresAt: action.expires_at,
  });

  let cancelPolling: () => void;

  const pollingPromise = new Promise<
  io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest
  >((resolve, reject) => {
    cancelPolling = pollForPrUpdate(pr, {
      diffCheck: (pr1, pr2) => !isEqual(
        isInternalPaymentRequest(pr1) ? pr1.actual_action : pr1.next_action,
        isInternalPaymentRequest(pr2) ? pr2.actual_action : pr2.next_action,
      ),
      onChange: resolve,
      onTimeout: reject,
    });
  });

  const iframe = createExecuteScriptElement({
    script: action.script,
  });

  resolvedContainer.appendChild(iframe);

  return Promise.any([
    callbackPromise.then((params): Promise<
    io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest
    > => {
      if (isNil(params.flow_payment_request_id)) {
        // this will reject this promise, we still have a chance with the polling below 🤞🏻
        throw new Error('handleExecuteScriptAction: inline callback received without PR id');
      }
      cancelPolling();
      return isInternalPaymentRequest(pr)
        ? fetchInternalPaymentRequest(params.flow_payment_request_id)
        : fetchPaymentRequest(params.flow_payment_request_id);
    }),
    pollingPromise,
  ]).finally(() => {
    hideLoadingSpinner();
    removeElement(iframe);
  });
}
